<template>
  <div class="page-with-subnav">
    <app-sub-navigation-bar
      back-label="Back to all tasks"
      @back="$router.push('/settings/task')"
    />
    <div class="main-container page-with-subnav__content">
      <div class="ui grid task-form-grid">
        <div class="sixteen wide column">
          <h1 class="task-form-heading">
            <i class="edit outline icon"></i>
            {{ pageTitle }}
          </h1>
        </div>
        <div class="sixteen wide column">
          <app-form-error></app-form-error>
          <form class="ui form task-form-inner" @submit.prevent>
            <nav class="task-stepper" aria-label="Task steps">
              <div class="task-stepper__row" role="list">
                <template v-for="(step, idx) in wizardSteps" :key="step">
                  <button
                    type="button"
                    class="task-stepper__btn"
                    role="listitem"
                    :class="{
                      'is-active': activeTab === idx,
                      'is-done': idx < activeTab,
                      'is-next': idx === activeTab + 1
                    }"
                    @click="goToTab(idx)"
                  >
                    <span class="task-stepper__num">
                      <span v-if="idx < activeTab" class="task-stepper__check">✓</span>
                      <span v-else class="task-stepper__numText">{{ idx + 1 }}</span>
                    </span>
                    <span class="task-stepper__label">{{ step }}</span>
                  </button>

                  <!-- Connector segment between steps -->
                  <span
                    v-if="idx < wizardSteps.length - 1"
                    class="task-stepper__connector"
                    :class="{ 'is-solid': idx <= activeTab }"
                    aria-hidden="true"
                  />
                </template>
              </div>
            </nav>

            <div v-show="activeTab === 0" class="task-step-panel">
                <h4 class="ui dividing header">Task Images</h4>
                <app-images
                  :parent_item="task"
                  :save="save"
                  @images="onImagesChange"
                  :can-upload="!disabled"
                  :show-upload-label="false"
                />

                <h4 class="ui dividing header">Task Details</h4>

                <div class="two fields">
                  <div class="field settings">
                    <label>Code</label>
                    <app-text-input
                      :model-value="task.code"
                      disabled
                    />
                  </div>
                  <div class="field settings">
                    <label>Type*</label>
                    <app-select
                      :defaultLabel="'Select type'"
                      :items="taskTypeOptions"
                      :value="typeSelectionId"
                      :disabled="disabled"
                      :readonly="disabled"
                      @selected="onTypeSelected"
                    />
                    <a
                      href="#"
                      class="task-type-help-link"
                      @click.prevent="showTypeHelp = true"
                    >Understand the different task types</a>
                  </div>
                </div>

                <div class="field settings">
                  <label>Title*</label>
                  <app-text-input
                    placeholder="Enter task title"
                    :model-value="task.title"
                    :disabled="overrideFieldsDisabled"
                    :readonly="overrideFieldsDisabled"
                    :error="validation('title')"
                    @update:model-value="task.title = $event"
                  />
                </div>

                <div class="field settings">
                  <label>Description*</label>
                  <app-text-area
                    rows="4"
                    placeholder="Enter task description (e.g. Install Flick Mixer)"
                    :model-value="task.description"
                    :disabled="overrideFieldsDisabled"
                    :readonly="overrideFieldsDisabled"
                    :error="validation('description')"
                    @update:model-value="task.description = $event"
                  />
                </div>

                <div class="field settings" v-if="isExcavation">
                  <label>Excavation Method*</label>
                  <app-select
                    :defaultLabel="'Select method'"
                    :items="excavationMethods"
                    :value="task.excavation_method"
                    :disabled="disabled"
                    :readonly="disabled"
                    @selected="task.excavation_method = $event.id"
                  />
                </div>

                <div class="two fields" v-if="isTimed">
                  <div class="field settings">
                    <label>Hourly Rate Multiplier*</label>
                    <app-select
                      :defaultLabel="''"
                      :items="$store.getters['task/getMultipliers']"
                      :disabled="overrideFieldsDisabled"
                      :readonly="overrideFieldsDisabled"
                      :value="task.hourly_rate_multiplier"
                      @selected="task.hourly_rate_multiplier = $event.id"
                    />
                  </div>
                  <div class="field settings">
                    <label>Time to Complete*</label>
                    <div class="task-time-complete">
                      <app-select
                        :defaultLabel="''"
                        :items="$store.getters['task/getTimeHours']"
                        :disabled="overrideFieldsDisabled"
                        :readonly="overrideFieldsDisabled"
                        :value="task.labour_time_hours"
                        @selected="task.labour_time_hours = $event.id"
                      />
                      <app-select
                        :defaultLabel="''"
                        :items="$store.getters['task/getTimeMinutes']"
                        :disabled="overrideFieldsDisabled"
                        :readonly="overrideFieldsDisabled"
                        :value="task.labour_time_minutes"
                        @selected="task.labour_time_minutes = $event.id"
                      />
                    </div>
                  </div>
                </div>

                <h4 class="ui dividing header">Pricing Overrides</h4>
                <div class="three fields">
                  <div class="field settings">
                    <label>Standard Sell Price</label>
                    <div class="ui left labeled input">
                      <div class="ui label">$</div>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        v-model="task.sell_price"
                        :disabled="priceFieldsDisabled"
                        :readonly="priceFieldsDisabled"
                      >
                    </div>
                  </div>
                  <div class="field settings">
                    <label>Add-on Sell Price</label>
                    <div class="ui left labeled input">
                      <div class="ui label">$</div>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        v-model="task.addon_sell_price"
                        :disabled="priceFieldsDisabled"
                        :readonly="priceFieldsDisabled"
                      >
                    </div>
                  </div>
                  <div class="field settings">
                    <label>Includes GST*</label>
                    <app-select
                      :defaultLabel="''"
                      :items="includesGstOptions"
                      :disabled="overrideFieldsDisabled"
                      :readonly="overrideFieldsDisabled"
                      :value="taskIncludesGst"
                      @selected="task.includes_gst = $event.id"
                    />
                  </div>
                </div>

                <template v-if="showTaskManagement">
                  <div class="task-management-actions">
                    <button
                      v-if="canResetPrices"
                      type="button"
                      class="ui negative basic button task-reset-default-btn"
                      :class="{ disabled: !hasPriceOverride }"
                      :disabled="!hasPriceOverride || resettingPrices"
                      @click="resetPricesToDefault"
                    >
                      <span
                        class="task-reset-default-btn__icon"
                        aria-hidden="true"
                      ></span>
                      Reset to Default
                    </button>
                    <button
                      v-if="canDelete"
                      type="button"
                      class="ui negative basic button"
                      @click="deleted"
                    >
                      <i class="trash alternate outline icon"></i>
                      Delete Task
                    </button>
                  </div>
                </template>
              </div>

              <div v-show="activeTab === 1" class="task-step-panel">
                <h4 class="ui header" :class="{ error: validation('categories') }">
                  Task Classification
                </h4>
                <p :class="{ error: validation('categories') }">
                  Select the categories you want the task to appear in when creating a quote.
                </p>

                <div class="ui grid">
                  <div class="five wide column">
                    <div class="ui vertical scrolling fluid tabular menu">
                      <a
                        v-for="category in $store.getters['category/getCategories']"
                        :key="category.id"
                        class="item"
                        :class="{ active: activeCategory === category.name }"
                        @click="categorySelected(category)"
                      >{{ category.name }}</a>
                    </div>
                  </div>
                  <div class="eleven wide stretched column">
                    <div class="ui sticky">
                      <table class="ui basic celled padded table">
                        <thead>
                          <tr>
                            <th>Description</th>
                            <th>Visible</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="category in subCategories" :key="category.id" v-if="activeCategory">
                            <td>{{ category.name }}</td>
                            <td>
                              <div class="classification-checkbox">
                                <input
                                  type="checkbox"
                                  :id="category.id + '-main'"
                                  :disabled="disabled"
                                  :readonly="disabled"
                                  :value="category.id"
                                  v-model="category.main"
                                  @change="subCategoryClicked(category, 'main', $event)"
                                >
                                <label :for="category.id + '-main'">Main Task</label>
                              </div>
                              <div class="classification-checkbox">
                                <input
                                  type="checkbox"
                                  :id="category.id + '-addon'"
                                  :disabled="disabled"
                                  :readonly="disabled"
                                  :value="category.id"
                                  v-model="category.addon"
                                  @change="subCategoryClicked(category, 'addon', $event)"
                                >
                                <label :for="category.id + '-addon'">Add On</label>
                              </div>
                            </td>
                          </tr>
                          <tr v-if="subCategories.length === 0 && !loading">
                            <td colspan="2" class="center aligned">
                              <p v-if="!activeCategory">Please select a category from the left to view its subcategories.</p>
                              <p v-else>No subcategories available for this category.</p>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                      <app-loader v-if="loading"></app-loader>
                    </div>
                  </div>
                </div>
              </div>

              <div v-show="activeTab === 2" class="task-step-panel">
                <!-- Timed / Untimed includes -->
                <div v-if="isTimed || isFixed" class="ui accordion task-includes-accordion">
                  <div class="title active">
                    <h4 class="ui header">
                      <i class="dropdown icon"></i> Included Items {{ materialCount }}
                    </h4>
                  </div>
                  <div class="content active">
                    <div class="field settings">
                      <app-text-input
                        icon="search"
                        placeholder="Search to add materials"
                        v-model="materialSearch"
                        :disabled="disabled"
                        :readonly="disabled"
                        @keyup="searchMaterials"
                      />
                    </div>
                    <div class="field settings">
                      <table class="ui basic celled padded table">
                        <thead>
                          <tr>
                            <th>Image</th>
                            <th>Description</th>
                            <th>Cost</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="material in materials.default" :key="'search-mat-' + material.id">
                            <td>
                              <img loading="lazy" :src="getImageUrl(material)" width="32" height="32">
                            </td>
                            <td>{{ material.description }}</td>
                            <td width="15%">
                              <app-select
                                :defaultLabel="''"
                                :items="$store.getters['material/getQty']"
                                :disabled="disabled"
                                :readonly="disabled"
                                :value="''"
                                @selected="updateTaskMaterials(material, $event, 'default')"
                              />
                            </td>
                            <td>${{ sellPriceIncGst(material) }}</td>
                          </tr>
                          <template v-if="!search">
                            <tr v-for="material in task.materials.default" :key="'mat-d-' + material.id">
                              <td>
                                <img loading="lazy" :src="getImageUrl(material)" width="32" height="32">
                              </td>
                              <td>{{ material.description }}</td>
                              <td width="15%">
                                <app-select
                                  :defaultLabel="''"
                                  :items="$store.getters['material/getQty']"
                                  :disabled="disabled"
                                  :readonly="disabled"
                                  :value="material.qty"
                                  @selected="updateTaskMaterials(material, $event, 'default')"
                                />
                              </td>
                              <td>${{ sellPriceIncGst(material) }}</td>
                              <td>
                                <i
                                  v-if="!disabled"
                                  class="times circle outline icon sidebar-remove"
                                  @click="updateTaskMaterials(material, 0, 'default')"
                                ></i>
                              </td>
                            </tr>
                            <tr v-for="material in task.materials.company" :key="'mat-c-' + material.id">
                              <td>
                                <img loading="lazy" :src="getImageUrl(material)" width="32" height="32">
                              </td>
                              <td>{{ material.description }}</td>
                              <td width="15%">
                                <app-select
                                  :defaultLabel="''"
                                  :items="$store.getters['material/getQty']"
                                  :disabled="disabled"
                                  :readonly="disabled"
                                  :value="material.qty"
                                  @selected="updateTaskMaterials(material, $event, 'company')"
                                />
                              </td>
                              <td>${{ sellPriceIncGst(material) }}</td>
                              <td>
                                <i
                                  v-if="!disabled"
                                  class="times circle outline icon sidebar-remove"
                                  @click="updateTaskMaterials(material, 0, 'company')"
                                ></i>
                              </td>
                            </tr>
                          </template>
                          <tr v-if="materials.default.length === 0 && materials.company.length === 0 && materialSearch.length > 4 && !loading">
                            <td colspan="5" class="center aligned">No materials found</td>
                          </tr>
                          <tr v-if="!hasSelectedMaterials && materialSearch.length <= 4 && !loading && !search">
                            <td colspan="5" class="center aligned">No materials have been added yet</td>
                          </tr>
                        </tbody>
                      </table>
                      <app-loader v-if="materials.default.length === 0 && loading"></app-loader>
                    </div>
                  </div>

                  <div class="ui divider"></div>

                  <div class="title">
                    <h4 class="ui header">
                      <i class="dropdown icon"></i> Variable Items {{ variableCount }}
                    </h4>
                  </div>
                  <div class="content">
                    <div class="field settings">
                      <app-text-input
                        icon="search"
                        placeholder="Search to add variables"
                        v-model="variableSearch"
                        :disabled="disabled"
                        :readonly="disabled"
                        @keyup="searchVariables"
                      />
                    </div>
                    <div class="field settings">
                      <table class="ui basic celled padded table">
                        <thead>
                          <tr>
                            <th>Image</th>
                            <th>Description</th>
                            <th>Type</th>
                            <th>Sell Price</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="variable in task.variables.default" :key="'var-d-' + (variable.uuid || variable.id)">
                            <td>
                              <img loading="lazy" :src="getImageUrl(variable)" width="32" height="32">
                            </td>
                            <td>{{ variable.description }}</td>
                            <td>{{ getTypeLabel(variable) }}</td>
                            <td>${{ sellPriceIncGst(variable) }}</td>
                            <td>
                              <i
                                v-if="!disabled"
                                class="times circle outline icon sidebar-remove"
                                @click="updateTaskVariables(variable, 0, 'default')"
                              ></i>
                            </td>
                          </tr>
                          <tr v-for="variable in task.variables.company" :key="'var-c-' + (variable.uuid || variable.id)">
                            <td>
                              <img loading="lazy" :src="getImageUrl(variable)" width="32" height="32">
                            </td>
                            <td>{{ variable.description }}</td>
                            <td>{{ getTypeLabel(variable) }}</td>
                            <td>${{ sellPriceIncGst(variable) }}</td>
                            <td>
                              <i
                                v-if="!disabled"
                                class="times circle outline icon sidebar-remove"
                                @click="updateTaskVariables(variable, 0, 'company')"
                              ></i>
                            </td>
                          </tr>
                          <tr v-for="variable in variables.default" :key="'var-s-' + variable.id">
                            <td>
                              <img loading="lazy" :src="getImageUrl(variable)" width="32" height="32">
                            </td>
                            <td>{{ variable.description }}</td>
                            <td>{{ getTypeLabel(variable) }}</td>
                            <td>${{ sellPriceIncGst(variable) }}</td>
                            <td>
                              <div class="ui basic button" @click="updateTaskVariables(variable, $event, 'default')">Add</div>
                            </td>
                          </tr>
                          <tr v-if="variables.default.length === 0 && variableSearch.length > 4 && !loading">
                            <td colspan="5" class="center aligned">Nothing found</td>
                          </tr>
                          <tr v-if="!hasSelectedVariables && variableSearch.length <= 4 && !loading && !search">
                            <td colspan="5" class="center aligned">No products have been added yet</td>
                          </tr>
                        </tbody>
                      </table>
                      <app-loader v-if="variables.default.length === 0 && loading"></app-loader>
                    </div>
                  </div>
                </div>

                <!-- Calculated (non-excavation) includes -->
                <div v-else-if="isCalculated && !isExcavation" class="ui accordion task-includes-accordion">
                  <template v-for="(section, sIdx) in includesSections" :key="'sec-' + section.parentType">
                    <div class="ui divider" v-if="sIdx > 0"></div>
                    <div class="title" :class="{ active: sIdx === 0 }">
                      <h4 class="ui header">
                        <i class="dropdown icon"></i>
                        {{ section.title }} {{ materialCountByParent(section.parentType) }}
                      </h4>
                    </div>
                    <div class="content" :class="{ active: sIdx === 0 }">
                      <div class="field settings">
                        <app-text-input
                          icon="search"
                          :placeholder="'Search to add ' + section.searchLabel"
                          v-model="materialSearch"
                          :disabled="disabled"
                          :readonly="disabled"
                          @keyup="searchMaterials"
                        />
                      </div>
                      <div class="field settings">
                        <table class="ui basic celled padded table">
                          <thead>
                            <tr>
                              <th>Image</th>
                              <th>Description</th>
                              <th>Item</th>
                              <th>Price</th>
                              <th>Time to Complete</th>
                              <th>Product/Labor Cost</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="material in materials.default"
                              :key="'calc-s-' + section.parentType + '-' + material.id"
                              v-show="Number(material.parent_type) === section.parentType"
                            >
                              <td>
                                <img :src="getImageUrl(material)" width="32" height="32">
                              </td>
                              <td>{{ material.description }}</td>
                              <td>{{ materialUnitLabel(material) }}</td>
                              <td>{{ formatCurrency(sellPriceIncGst(material)) }}</td>
                              <td>
                                <app-select
                                  :defaultLabel="''"
                                  :items="$store.getters['material/getTimeComplete']"
                                  :disabled="disabled"
                                  :readonly="disabled"
                                  :value="''"
                                  @selected="updateTaskMaterials(material, $event, 'default')"
                                />
                              </td>
                              <td>
                                <div>{{ formatCurrency(materialTotalPrice(material)) }}</div>
                              </td>
                            </tr>
                            <template v-if="!search">
                              <tr
                                v-for="material in task.materials.default"
                                :key="'calc-d-' + section.parentType + '-' + material.id"
                                v-show="Number(material.parent_type) === section.parentType"
                              >
                                <td>
                                  <img :src="getImageUrl(material)" width="32" height="32">
                                </td>
                                <td>{{ material.description }}</td>
                                <td>{{ materialUnitLabel(material) }}</td>
                                <td>{{ formatCurrency(sellPriceIncGst(material)) }}</td>
                                <td>
                                  <app-select
                                    :defaultLabel="''"
                                    :items="$store.getters['material/getTimeComplete']"
                                    :disabled="disabled"
                                    :readonly="disabled"
                                    :value="material.time_complete"
                                    @selected="updateTaskMaterials(material, $event, 'default')"
                                  />
                                </td>
                                <td>
                                  <div>{{ formatCurrency(materialTotalPrice(material)) }}</div>
                                </td>
                                <td>
                                  <i
                                    v-if="!disabled"
                                    class="times circle outline icon sidebar-remove"
                                    @click="updateTaskMaterials(material, 0, 'default')"
                                  ></i>
                                </td>
                              </tr>
                              <tr
                                v-for="material in task.materials.company"
                                :key="'calc-c-' + section.parentType + '-' + material.id"
                                v-show="Number(material.parent_type) === section.parentType"
                              >
                                <td>
                                  <img :src="getImageUrl(material)" width="32" height="32">
                                </td>
                                <td>{{ material.description }}</td>
                                <td>{{ materialUnitLabel(material) }}</td>
                                <td>{{ formatCurrency(sellPriceIncGst(material)) }}</td>
                                <td>
                                  <app-select
                                    :defaultLabel="''"
                                    :items="$store.getters['material/getTimeComplete']"
                                    :disabled="disabled"
                                    :readonly="disabled"
                                    :value="material.time_complete"
                                    @selected="updateTaskMaterials(material, $event, 'company')"
                                  />
                                </td>
                                <td>
                                  <div>{{ formatCurrency(materialTotalPrice(material)) }}</div>
                                </td>
                                <td>
                                  <i
                                    v-if="!disabled"
                                    class="times circle outline icon sidebar-remove"
                                    @click="updateTaskMaterials(material, 0, 'company')"
                                  ></i>
                                </td>
                              </tr>
                            </template>
                            <tr v-if="materials.default.length === 0 && materials.company.length === 0 && materialSearch.length >= 3 && !loading">
                              <td colspan="7" class="center aligned">No products found</td>
                            </tr>
                            <tr v-if="!hasSelectedMaterials && materialSearch.length <= 3 && !loading && !search">
                              <td colspan="7" class="center aligned">{{ section.emptyMsg }}</td>
                            </tr>
                          </tbody>
                        </table>
                        <app-loader v-if="materials.default.length === 0 && loading"></app-loader>
                      </div>
                    </div>
                  </template>
                </div>

                <!-- Excavation empty state -->
                <div v-else-if="isExcavation" class="task-excavation-empty">
                  <p>Dimensions and soil type are entered when quoting.</p>
                </div>
              </div>
          </form>
        </div>
      </div>
    </div>

    <app-sticky-form-footer sticky>
      <template #left>
        <button
          v-if="activeTab > 0"
          type="button"
          class="ui basic button"
          @click="prevTab"
        >Previous</button>
      </template>
      <button type="button" class="ui primary button" @click="nextOrSave">{{ saveNextLabel }}</button>
    </app-sticky-form-footer>

    <app-standard-modal
      :open="showTypeHelp"
      title="Task types"
      title-id="task-type-help-title"
      size="medium"
      @close="showTypeHelp = false"
    >
      <div
        v-for="item in taskTypeHelpItems"
        :key="item.title"
        class="smardie-modal-help-item smardie-modal-help-item--emoji"
      >
        <span class="smardie-modal-help-emoji" aria-hidden="true">{{ item.emoji }}</span>
        <div>
          <h3>{{ item.title }}</h3>
          <p>{{ item.description }}</p>
        </div>
      </div>
      <template #footer>
        <button type="button" class="ui primary button" @click="showTypeHelp = false">OK</button>
      </template>
    </app-standard-modal>
  </div>
</template>

<script>
import axios from 'axios'
import auth from '../../helpers/auth'
import array from '../../helpers/array'
import SubNavigationBar from '../../components/common/SubNavigationBar.vue'
import { INCLUDES_GST_OPTIONS, includesGstFlag } from '../../helpers/productPricing'
import { normalizeForCalculatedIncludes } from '../../helpers/materialFamily'
import { resolveItemImageUrl, withNormalizedImages, itemHasResolvableImage } from '../../helpers/itemImageUrl'
import {
  TASK_TYPE_OPTIONS,
  TASK_TYPE_HELP_ITEMS,
  EXCAVATION_METHODS,
  parseTaskTypeSelection,
  taskTypeSelectionId,
  getCalculatedLabels,
  isTimedType,
  isFixedType,
  isCalculatedType,
  isExcavationTemplate,
  normalizeTemplate
} from '../../helpers/taskTypes'

const CATALOG_LABOUR_PREVIEW_FALLBACK = 100
const DEFAULT_GST_RATE = 0.10

function emptyTask () {
  return {
    id: null,
    code: '',
    title: '',
    description: '',
    hourly_rate_multiplier: 1,
    labour_hourly_rate: 0,
    labour_time_hours: 0,
    labour_time_minutes: 0,
    categories: [],
    materials: { default: [], company: [] },
    products: { default: [], company: [] },
    variables: { default: [], company: [] },
    images: [],
    type: 'time_based',
    template: null,
    excavation_method: 'hand',
    cost: 0.00,
    sell_price: 0.00,
    addon_sell_price: 0.00,
    default_sell_price: 0.00,
    default_addon_sell_price: 0.00,
    default_name: '',
    default_description: '',
    default_hourly_rate_multiplier: 1,
    default_labour_time_hours: 0,
    default_labour_time_minutes: 0,
    default_includes_gst: 1,
    has_price_override: false,
    includes_gst: 1,
    is_active: true,
    company_id: undefined,
    default: false
  }
}

export default {
  name: 'TaskForm',
  components: {
    'app-sub-navigation-bar': SubNavigationBar
  },
  data () {
    return {
      task: emptyTask(),
      taskTypeHelpItems: TASK_TYPE_HELP_ITEMS,
      subCategories: [],
      activeCategory: null,
      loading: false,
      activeTab: 0,
      materials: { default: [], company: [] },
      variables: { default: [], company: [] },
      materialSearch: '',
      variableSearch: '',
      search: false,
      searchTimeout: null,
      jobHourlyRate: 0,
      hydratedImageIds: {},
      includesGstOptions: INCLUDES_GST_OPTIONS,
      taskTypeOptions: TASK_TYPE_OPTIONS,
      excavationMethods: EXCAVATION_METHODS,
      wizardSteps: ['Details', 'Classifications', 'Includes'],
      showTypeHelp: false,
      saving: false,
      resettingPrices: false
    }
  },
  computed: {
    isNew () {
      return this.$route.name === 'TaskNew'
    },
    pageTitle () {
      return this.isNew ? 'New Task' : 'Manage Task'
    },
    isCatalogDefault () {
      return !!(this.task && this.task.id && (this.task.default || this.task.company_id == null || this.task.company_id === ''))
    },
    disabled () {
      return !this.$store.getters['user/isCatalogEditor'] && this.isCatalogDefault
    },
    canOverrideCatalogTask () {
      // Company owners on Smardie default tasks: limited field overrides, not full edit.
      return !!(this.isCatalogDefault && !this.$store.getters['user/isCatalogEditor'])
    },
    overrideFieldsDisabled () {
      if (this.canOverrideCatalogTask) return false
      return this.disabled
    },
    priceFieldsDisabled () {
      return this.overrideFieldsDisabled
    },
    hasPriceOverride () {
      return !!(this.task && this.task.has_price_override)
    },
    canResetPrices () {
      return this.canOverrideCatalogTask
    },
    canDelete () {
      if (!this.task || !this.task.id) return false
      if (this.$store.getters['user/isCatalogEditor']) return true
      return !this.isCatalogDefault
    },
    showTaskManagement () {
      return this.canDelete || this.canResetPrices
    },
    isTimed () {
      return isTimedType(this.task.type)
    },
    isFixed () {
      return isFixedType(this.task.type)
    },
    isCalculated () {
      return isCalculatedType(this.task.type)
    },
    isExcavation () {
      return this.isCalculated && isExcavationTemplate(this.task.template)
    },
    calculatedLabels () {
      return getCalculatedLabels(this.task.template)
    },
    typeSelectionId () {
      return taskTypeSelectionId(this.task.type, this.task.template)
    },
    taskIncludesGst () {
      return includesGstFlag(this.task && this.task.includes_gst) ? 1 : 0
    },
    saveNextLabel () {
      return this.activeTab >= 2 ? 'Save' : 'Next'
    },
    materialCount () {
      const d = (this.task.materials && this.task.materials.default) || []
      const c = (this.task.materials && this.task.materials.company) || []
      return '(' + (d.length + c.length) + ')'
    },
    variableCount () {
      const d = (this.task.variables && this.task.variables.default) || []
      const c = (this.task.variables && this.task.variables.company) || []
      return '(' + (d.length + c.length) + ')'
    },
    hasSelectedMaterials () {
      const d = (this.task.materials && this.task.materials.default) || []
      const c = (this.task.materials && this.task.materials.company) || []
      return d.length > 0 || c.length > 0
    },
    hasSelectedVariables () {
      const d = (this.task.variables && this.task.variables.default) || []
      const c = (this.task.variables && this.task.variables.company) || []
      return d.length > 0 || c.length > 0
    },
    includesSections () {
      const labels = this.calculatedLabels
      const sections = []
      if (labels.hasPrimary) {
        sections.push({
          parentType: 1,
          title: (labels.primaryPlural || labels.primary) + ' Available for Selection',
          searchLabel: (labels.primaryPlural || 'products').toLowerCase(),
          emptyMsg: 'No products have been added yet'
        })
      }
      if (labels.hasFittings) {
        sections.push({
          parentType: 2,
          title: (labels.fittings || 'Fittings') + ' Available for Selection',
          searchLabel: (labels.fittings || 'fittings').toLowerCase(),
          emptyMsg: 'No fittings have been added yet'
        })
      }
      if (labels.hasFixings) {
        sections.push({
          parentType: 3,
          title: (labels.fixings || 'Fixings') + ' Available for Selection',
          searchLabel: (labels.fixings || 'fixings').toLowerCase(),
          emptyMsg: 'No fixings have been added yet'
        })
      }
      return sections
    },
    taskVariablesWatch () {
      return {
        materials: this.task.materials,
        products: this.task.products,
        labour_time_hours: this.task.labour_time_hours,
        labour_time_minutes: this.task.labour_time_minutes
      }
    }
  },
  watch: {
    '$route' () {
      this.bootstrapFromRoute()
    },
    taskVariablesWatch () {
      this.calculateCost()
      this.hydrateTaskItemImages()
    },
    activeTab (tab) {
      if (tab === 2) this.initIncludesAccordion()
    }
  },
  created () {
    auth.check(this)
    this.$store.commit('setFooter', false)
    this.bootstrapFromRoute()
    this.loadJobHourlyRate()
  },
  beforeUnmount () {
    this.destroyIncludesAccordion()
    this.$store.commit('setFooter', true)
  },
  methods: {
    initIncludesAccordion () {
      this.$nextTick(() => {
        if (!this.$el || typeof $ !== 'function') return
        const $acc = $(this.$el).find('.task-includes-accordion')
        if (!$acc.length) return
        // Destroy any prior Semantic binding so we never re-init in an update loop
        if ($acc.data('module-accordion')) {
          try { $acc.accordion('destroy') } catch (e) { /* ignore */ }
        }
        $acc.accordion()
      })
    },
    destroyIncludesAccordion () {
      try {
        if (!this.$el || typeof $ !== 'function') return
        const $acc = $(this.$el).find('.task-includes-accordion')
        if ($acc.length && $acc.data('module-accordion')) {
          $acc.accordion('destroy')
        }
      } catch (e) { /* ignore teardown races during route errors */ }
    },
    bootstrapFromRoute () {
      this.activeTab = 0
      this.clearSearch()
      this.subCategories = []
      this.activeCategory = null
      this.showTypeHelp = false
      if (this.isNew) {
        this.task = emptyTask()
        this.loadNextTaskCode()
        return
      }
      const id = this.$route.params.id
      if (!id) return
      axios.get('/api/tasks/' + id).then(response => {
        const taskData = response.data.data || response.data
        this.applyLoadedTask(taskData)
      }).catch(() => {
        this.errorNotification('Failed to load task')
        this.$router.replace('/settings/task')
      })
    },
    applyLoadedTask (taskData) {
      taskData.title = taskData.name || taskData.title || ''
      if (taskData.is_active === undefined || taskData.is_active === null) {
        taskData.is_active = true
      } else {
        taskData.is_active = Boolean(taskData.is_active)
      }
      taskData.includes_gst = includesGstFlag(taskData.includes_gst) ? 1 : 0
      if (taskData.sell_price == null || taskData.sell_price === '') {
        taskData.sell_price = 0
      }
      if (taskData.addon_sell_price == null || taskData.addon_sell_price === '') {
        taskData.addon_sell_price = 0
      }
      if (taskData.default_sell_price == null || taskData.default_sell_price === '') {
        taskData.default_sell_price = taskData.sell_price
      }
      if (taskData.default_addon_sell_price == null || taskData.default_addon_sell_price === '') {
        taskData.default_addon_sell_price = taskData.addon_sell_price
      }
      if (taskData.default_name == null || taskData.default_name === '') {
        taskData.default_name = taskData.name || taskData.title || ''
      }
      if (taskData.default_description == null) {
        taskData.default_description = taskData.description || ''
      }
      if (taskData.default_hourly_rate_multiplier == null) {
        taskData.default_hourly_rate_multiplier = taskData.hourly_rate_multiplier
      }
      if (taskData.default_labour_time_hours == null) {
        taskData.default_labour_time_hours = taskData.labour_time_hours
      }
      if (taskData.default_labour_time_minutes == null) {
        taskData.default_labour_time_minutes = taskData.labour_time_minutes
      }
      if (taskData.default_includes_gst == null) {
        taskData.default_includes_gst = includesGstFlag(taskData.includes_gst) ? 1 : 0
      }
      taskData.has_price_override = Boolean(taskData.has_price_override)
      if (taskData.hourly_rate_multiplier == null) {
        taskData.hourly_rate_multiplier = 1
      } else {
        taskData.hourly_rate_multiplier = parseFloat(taskData.hourly_rate_multiplier)
      }
      if (!taskData.materials) taskData.materials = { default: [], company: [] }
      if (!taskData.variables) taskData.variables = { default: [], company: [] }
      if (!taskData.products) taskData.products = { default: [], company: [] }
      if (!taskData.categories) taskData.categories = []
      if (!taskData.images) taskData.images = []
      taskData.default = taskData.company_id == null || taskData.company_id === ''
      if (isCalculatedType(taskData.type)) {
        taskData.template = normalizeTemplate(taskData.template, 'calculated')
      } else {
        taskData.template = null
      }
      if (taskData.template === 'excavation' && !taskData.excavation_method) {
        taskData.excavation_method = 'hand'
      }
      this.task = Object.assign(emptyTask(), taskData)
      if (this.task.materials) {
        this.task.materials.default = normalizeForCalculatedIncludes(this.task.materials.default)
        this.task.materials.company = normalizeForCalculatedIncludes(this.task.materials.company)
      }
      this.activeTab = 0
      this.calculateCost()
      this.hydrateTaskItemImages()
    },
    loadJobHourlyRate () {
      axios.get('/api/setting/settings').then(response => {
        const d = response.data || {}
        this.jobHourlyRate = this.parseRate(d.hourly_rate)
        if (this.jobHourlyRate > 0 && this.task && (!this.task.labour_hourly_rate || Number(this.task.labour_hourly_rate) === 0)) {
          this.task.labour_hourly_rate = this.jobHourlyRate
        }
        this.calculateCost()
      }).catch(() => {
        this.jobHourlyRate = 0
        this.calculateCost()
      })
    },
    loadNextTaskCode () {
      axios.get('/api/tasks/next-code').then(response => {
        const code = response && response.data && response.data.code
        if (this.isNew && code) this.task.code = code
      }).catch(() => {
        // Saving still generates the code server-side if this preview request fails.
      })
    },
    onTypeSelected (event) {
      const parsed = parseTaskTypeSelection(event.id)
      this.task.type = parsed.type
      this.task.template = parsed.template
      this.clearSearch()
      this.calculateCost()
    },
    onImagesChange (images) {
      this.task.images = Array.isArray(images) ? images : []
    },
    goToTab (idx) {
      if (idx < 0 || idx > 2) return
      // Allow freely navigating to previous steps or already-reached; also next via footer
      this.activeTab = idx
      this.clearSearch(false)
    },
    nextOrSave () {
      if (this.activeTab >= 2) {
        this.save()
        return
      }
      this.activeTab = this.activeTab + 1
      this.clearSearch(false)
    },
    prevTab () {
      if (this.activeTab > 0) {
        this.activeTab = this.activeTab - 1
        this.clearSearch(false)
      }
    },
    materialCountByParent (parentType) {
      let count = 0
      const check = (list) => {
        (list || []).forEach(m => {
          if (Number(m.parent_type) === parentType) count++
        })
      }
      check(this.task.materials && this.task.materials.default)
      check(this.task.materials && this.task.materials.company)
      return '(' + count + ')'
    },
    formatCurrency (value) {
      const n = parseFloat(value)
      return '$' + (Number.isFinite(n) ? n.toFixed(2) : '0.00')
    },
    parseRate (value) {
      if (value == null) return 0
      const cleaned = String(value).replace(/[^0-9.-]/g, '')
      const parsed = parseFloat(cleaned)
      return isNaN(parsed) ? 0 : parsed
    },
    effectiveHourlyRate () {
      const jobRate = this.parseRate(this.jobHourlyRate)
      const taskRate = this.parseRate(this.task && this.task.labour_hourly_rate)
      if (jobRate || taskRate) return jobRate || taskRate
      const isCatalogEditor = this.$store.getters['user/isCatalogEditor'] && this.isCatalogDefault
      if (isCatalogEditor || (this.$store.getters['user/isCatalogEditor'] && this.isNew)) {
        const fromBuild = this.parseRate(process.env.VUE_APP_CATALOG_LABOUR_HOURLY_RATE)
        if (fromBuild > 0) return fromBuild
        return CATALOG_LABOUR_PREVIEW_FALLBACK
      }
      return 0
    },
    labourHourlyRateForSave () {
      // Catalog tasks always save 0 so each company’s Job Pricing applies at fetch time.
      if (this.$store.getters['user/isCatalogEditor'] && (this.isCatalogDefault || this.isNew)) {
        return 0
      }
      return this.effectiveHourlyRate()
    },
    materialTotalPrice (material) {
      const sellPrice = this.sellPriceIncGst(material)
      const minutes = parseFloat(material.time_complete) || 0
      const hourlyRate = this.effectiveHourlyRate()
      return sellPrice + ((hourlyRate / 60) * minutes)
    },
    getImageUrl (item) {
      const placeholder = require('../../assets/img/no-image-available.jpg')
      return resolveItemImageUrl(item, this.$store.getters['getImageUrl'], placeholder)
    },
    materialUnitLabel (material) {
      const rawUnit = (material.unit_of_measure_name || material.unit || '').toString().trim()
      const normalized = rawUnit.toLowerCase()
      if (normalized === 'lm' || normalized === 'lineal metre' || normalized === 'linear metre') {
        return 'Lineal Metre'
      }
      if (normalized === 'ea' || normalized === 'each') {
        if (Number(material.parent_type) === 1) return 'Lineal Metre'
        return 'Each'
      }
      if (rawUnit) return rawUnit
      if (Number(material.parent_type) === 1) return 'Lineal Metre'
      return 'Each'
    },
    getTypeLabel (item) {
      if (!item) return ''
      if (item.type_name_full && String(item.type_name_full).trim() !== '') return item.type_name_full
      const typeId = item.type
      const types = this.$store.getters['material/getTypes'] || []
      const match = types.find(t => String(t.id) === String(typeId))
      return match && match.name ? match.name : (typeId != null ? String(typeId) : '')
    },
    sellPriceIncGst (item) {
      if (!item) return 0
      const cost = parseFloat(item.cost)
      const sell = parseFloat(item.sell_price)
      const hasSell = Number.isFinite(sell) && sell > 0
      if (!hasSell) {
        const base = Number.isFinite(cost) ? cost : 0
        return parseFloat((base * (1 + DEFAULT_GST_RATE)).toFixed(2))
      }
      if (Number.isFinite(cost) && Math.abs(sell - cost) < 0.0001) {
        return parseFloat((cost * (1 + DEFAULT_GST_RATE)).toFixed(2))
      }
      return sell
    },
    ensureSellPriceIncGst (item) {
      if (!item) return
      item.sell_price = this.sellPriceIncGst(item)
    },
    hydrateTaskItemImages () {
      const base = this.$store.getters['getImageUrl']
      if (!this._hydratingImageIds) this._hydratingImageIds = {}
      const inspect = (item, listRef) => {
        if (!item || !item.id) return
        const normalized = withNormalizedImages(item)
        if (normalized !== item && normalized.images != null) {
          item.images = normalized.images
        }
        // Only skip when a real thumbnail URL resolves — empty [{}] must still
        // rehydrate from GET /product/:id (same as Quote Builder Select Product).
        if (itemHasResolvableImage(item, base)) {
          this.hydratedImageIds[item.id] = true
          return
        }
        if (this.hydratedImageIds[item.id] || this._hydratingImageIds[item.id]) return
        this._hydratingImageIds[item.id] = true
        axios.get('/product/' + item.id).then((response) => {
          const payload = response && response.data
          const product = payload && payload.product ? payload.product : payload
          // Always mark attempted so task watch does not re-fetch forever
          this.hydratedImageIds[item.id] = true
          if (!product || product.images == null) return
          const next = withNormalizedImages({ images: product.images })
          if (!next.images || !itemHasResolvableImage(next, base)) return
          item.images = next.images
          // Replace array slot so Vue re-binds :src after async hydrate
          if (listRef && Array.isArray(listRef.list)) {
            const idx = listRef.list.findIndex(row => row && row.id === item.id)
            if (idx !== -1) {
              listRef.list.splice(idx, 1, Object.assign({}, listRef.list[idx], { images: next.images }))
            }
          }
        }).catch(() => {
          this.hydratedImageIds[item.id] = true
        }).finally(() => {
          delete this._hydratingImageIds[item.id]
        })
      }
      const mats = (this.task && this.task.materials) || { default: [], company: [] }
      const vars = (this.task && this.task.variables) || { default: [], company: [] }
      const searchMats = (this.materials) || { default: [], company: [] }
      const searchVars = (this.variables) || { default: [], company: [] }
      ;(mats.default || []).forEach(item => inspect(item, { list: mats.default }))
      ;(mats.company || []).forEach(item => inspect(item, { list: mats.company }))
      ;(vars.default || []).forEach(item => inspect(item, { list: vars.default }))
      ;(vars.company || []).forEach(item => inspect(item, { list: vars.company }))
      ;(searchMats.default || []).forEach(item => inspect(item, { list: searchMats.default }))
      ;(searchMats.company || []).forEach(item => inspect(item, { list: searchMats.company }))
      ;(searchVars.default || []).forEach(item => inspect(item, { list: searchVars.default }))
      ;(searchVars.company || []).forEach(item => inspect(item, { list: searchVars.company }))
    },
    validation (field) {
      if (field === 'title') return !this.task.title || this.task.title.trim() === ''
      if (field === 'description') return !this.task.description || this.task.description.trim() === ''
      if (field === 'categories') return !this.task.categories || this.task.categories.length === 0
      if (field === 'hourly_rate_multiplier') {
        return !this.task.hourly_rate_multiplier || this.task.hourly_rate_multiplier <= 0
      }
      return false
    },
    save () {
      // Default-task overrides must run before the generic read-only guard.
      if (this.canOverrideCatalogTask) {
        this.savePriceOverrides()
        return
      }
      if (this.disabled) {
        this.warningNotification('This task is read-only. Use Custom tasks to create your own editable version.')
        return
      }
      if (this.validation('title')) {
        this.errorNotification('Please enter a task title.')
        return
      }
      if (this.validation('description')) {
        this.errorNotification('Please enter a task description.')
        return
      }
      if (this.validation('categories')) {
        this.errorNotification('Please select at least one category on the Classifications step.')
        this.activeTab = 1
        return
      }
      if (this.isTimed && this.validation('hourly_rate_multiplier')) {
        this.errorNotification('Please select a valid hourly rate multiplier.')
        return
      }

      const backendType = this.task.type || 'time_based'
      const template = isCalculatedType(backendType)
        ? (normalizeTemplate(this.task.template, 'calculated') || 'service_line')
        : null

      const payload = {
        code: this.task.code || undefined,
        name: this.task.title,
        description: this.task.description,
        hourly_rate_multiplier: this.task.hourly_rate_multiplier,
        labour_hourly_rate: this.labourHourlyRateForSave(),
        labour_time_hours: this.task.labour_time_hours,
        labour_time_minutes: this.task.labour_time_minutes,
        cost: this.task.cost,
        sell_price: this.task.sell_price || 0.00,
        addon_sell_price: this.task.addon_sell_price || 0.00,
        includes_gst: includesGstFlag(this.task.includes_gst),
        type: backendType,
        template: template,
        excavation_method: (template === 'excavation') ? (this.task.excavation_method || 'hand') : null,
        is_active: Boolean(this.task.is_active),
        images: this.task.images || [],
        categories: (this.task.categories || []).map(cat => ({ id: cat.id, type: cat.type })),
        materials: this.task.materials || { default: [], company: [] },
        variables: this.task.variables || { default: [], company: [] }
      }

      if (this.saving) return
      this.saving = true

      const req = this.task.id
        ? axios.put('/api/tasks/' + this.task.id, payload)
        : axios.post('/api/tasks', payload)

      req.then(() => {
        this.successNotification(this.task.id ? 'Task updated successfully' : 'Task created successfully')
        this.$router.push('/settings/task')
      }).catch(error => {
        const msg = (error.response && error.response.data && error.response.data.error) || error.message || 'Failed to save task'
        this.errorNotification(msg)
      }).finally(() => {
        this.saving = false
      })
    },
    moneyEquals (a, b) {
      return Math.abs((parseFloat(a) || 0) - (parseFloat(b) || 0)) < 0.005
    },
    savePriceOverrides () {
      if (!this.task || !this.task.id) return
      if (this.validation('title')) {
        this.errorNotification('Please enter a task title.')
        return
      }
      if (this.validation('description')) {
        this.errorNotification('Please enter a task description.')
        return
      }
      if (this.isTimed && this.validation('hourly_rate_multiplier')) {
        this.errorNotification('Please select a valid hourly rate multiplier.')
        return
      }
      if (this.saving) return
      this.saving = true
      const payload = {
        name: (this.task.title || '').trim(),
        description: this.task.description || '',
        hourly_rate_multiplier: parseFloat(this.task.hourly_rate_multiplier) || 1,
        labour_time_hours: Number(this.task.labour_time_hours) || 0,
        labour_time_minutes: Number(this.task.labour_time_minutes) || 0,
        includes_gst: includesGstFlag(this.task.includes_gst),
        sell_price: parseFloat(this.task.sell_price) || 0,
        addon_sell_price: parseFloat(this.task.addon_sell_price) || 0
      }
      const matchesDefault = this.taskOverridesMatchDefaults(payload)

      const req = matchesDefault
        ? axios.delete('/api/tasks/' + this.task.id + '/price-overrides')
        : axios.put('/api/tasks/' + this.task.id + '/price-overrides', payload)

      req.then((response) => {
        this.applyTaskOverrideResponse((response.data && response.data.data) || {})
        this.successNotification(matchesDefault ? 'Task saved' : 'Task overrides updated')
        this.$router.push('/settings/task')
      }).catch(error => {
        const msg = (error.response && error.response.data && error.response.data.error) || error.message || 'Failed to save task overrides'
        this.errorNotification(msg)
      }).finally(() => {
        this.saving = false
      })
    },
    taskOverridesMatchDefaults (payload) {
      const task = this.task || {}
      const defaultIncludesGst = includesGstFlag(
        task.default_includes_gst != null ? task.default_includes_gst : task.includes_gst
      )
      return (
        String(payload.name || '').trim() === String(task.default_name || task.title || '').trim() &&
        String(payload.description || '') === String(task.default_description || '') &&
        this.moneyEquals(payload.hourly_rate_multiplier, task.default_hourly_rate_multiplier) &&
        Number(payload.labour_time_hours || 0) === Number(task.default_labour_time_hours || 0) &&
        Number(payload.labour_time_minutes || 0) === Number(task.default_labour_time_minutes || 0) &&
        includesGstFlag(payload.includes_gst) === defaultIncludesGst &&
        this.moneyEquals(payload.sell_price, task.default_sell_price) &&
        this.moneyEquals(payload.addon_sell_price, task.default_addon_sell_price)
      )
    },
    applyTaskOverrideResponse (data) {
      if (!data || !this.task) return
      if (data.name != null) this.task.title = data.name
      if (data.description != null) this.task.description = data.description
      if (data.hourly_rate_multiplier != null) {
        this.task.hourly_rate_multiplier = parseFloat(data.hourly_rate_multiplier)
      }
      if (data.labour_time_hours != null) this.task.labour_time_hours = data.labour_time_hours
      if (data.labour_time_minutes != null) this.task.labour_time_minutes = data.labour_time_minutes
      if (data.includes_gst != null) {
        this.task.includes_gst = includesGstFlag(data.includes_gst) ? 1 : 0
      }
      if (data.sell_price != null) this.task.sell_price = data.sell_price
      if (data.addon_sell_price != null) this.task.addon_sell_price = data.addon_sell_price
      if (data.default_name != null) this.task.default_name = data.default_name
      if (data.default_description != null) this.task.default_description = data.default_description
      if (data.default_hourly_rate_multiplier != null) {
        this.task.default_hourly_rate_multiplier = parseFloat(data.default_hourly_rate_multiplier)
      }
      if (data.default_labour_time_hours != null) {
        this.task.default_labour_time_hours = data.default_labour_time_hours
      }
      if (data.default_labour_time_minutes != null) {
        this.task.default_labour_time_minutes = data.default_labour_time_minutes
      }
      if (data.default_includes_gst != null) {
        this.task.default_includes_gst = includesGstFlag(data.default_includes_gst) ? 1 : 0
      }
      if (data.default_sell_price != null) this.task.default_sell_price = data.default_sell_price
      if (data.default_addon_sell_price != null) {
        this.task.default_addon_sell_price = data.default_addon_sell_price
      }
      if (data.has_price_override != null) {
        this.task.has_price_override = Boolean(data.has_price_override)
      }
    },
    resetPricesToDefault () {
      if (!this.canResetPrices || !this.hasPriceOverride || !this.task || !this.task.id) return
      this.swal({
        title: 'Reset to default?',
        text: 'Your company overrides for this task will be cleared. Future Smardie catalog changes will apply again.',
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#FF4C4C',
        confirmButtonClass: 'swal2-confirm-destructive',
        confirmButtonText: 'Yes, Reset'
      }).then((result) => {
        if (!result.value) return
        if (this.resettingPrices) return
        this.resettingPrices = true
        axios.delete('/api/tasks/' + this.task.id + '/price-overrides').then((response) => {
          this.applyTaskOverrideResponse((response.data && response.data.data) || {})
          this.successNotification('Task reset to catalog defaults')
          this.$router.push('/settings/task')
        }).catch(error => {
          const msg = (error.response && error.response.data && error.response.data.error) || error.message || 'Failed to reset task overrides'
          this.errorNotification(msg)
        }).finally(() => {
          this.resettingPrices = false
        })
      })
    },
    isSameTaskItem (a, b) {
      if (!a || !b) return false
      if (a.uuid != null && b.uuid != null && String(a.uuid) !== '' && String(b.uuid) !== '') {
        return String(a.uuid) === String(b.uuid)
      }
      if (a.id == null || b.id == null) return false
      return String(a.id) === String(b.id)
    },
    ensureItemUuid (item) {
      if (!item || item.uuid != null && String(item.uuid) !== '') return item
      const suffix = Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8)
      item.uuid = 'item-' + (item.id != null ? item.id : 'x') + '-' + suffix
      return item
    },
    updateTaskMaterials (material, event, type) {
      if (this.disabled) return
      const selectedValue = event && typeof event === 'object' && event.id !== undefined
        ? Number(event.id)
        : Number(event)
      const bucket = type === 'company' ? 'company' : 'default'

      if (selectedValue > 0) {
        this.ensureSellPriceIncGst(material)
        this.ensureItemUuid(material)
        if (this.isCalculated) {
          material.time_complete = selectedValue
        } else {
          material.qty = selectedValue
        }
        const list = this.task.materials[bucket] || []
        const idx = list.findIndex(item => this.isSameTaskItem(item, material))
        if (idx === -1) {
          list.push(material)
        } else {
          list.splice(idx, 1, material)
        }
        this.task.materials[bucket] = list
        // Keep search results open so multiple products can be added without re-searching
        this.materials.default = (this.materials.default || []).filter(m => !this.isSameTaskItem(m, material))
        this.materials.company = (this.materials.company || []).filter(m => !this.isSameTaskItem(m, material))
      } else {
        // Remove only the clicked row (never filter by missing uuid — that clears the whole list)
        this.task.materials[bucket] = (this.task.materials[bucket] || []).filter(
          item => !this.isSameTaskItem(item, material)
        )
      }
    },
    updateTaskVariables (variable, event, type) {
      if (this.disabled) return
      const bucket = type === 'company' ? 'company' : 'default'
      if (event === 0) {
        this.task.variables[bucket] = (this.task.variables[bucket] || []).filter(
          item => !this.isSameTaskItem(item, variable)
        )
        return
      }
      this.variables[bucket] = (this.variables[bucket] || []).filter(item => !this.isSameTaskItem(item, variable))
      this.ensureSellPriceIncGst(variable)
      this.ensureItemUuid(variable)
      this.task.variables[bucket].push(variable)
    },
    categorySelected (category) {
      this.activeCategory = category.name
      this.getSubCategories(category)
    },
    subCategoryClicked (category, type, event) {
      if (event.target.checked) {
        const categoryToCheck = { id: category.id, type: type }
        if (!array.existsById(this.task.categories, categoryToCheck)) {
          this.task.categories.push({ id: category.id, type: type })
        } else {
          const existingIndex = this.task.categories.findIndex(cat => cat.id === category.id)
          if (existingIndex !== -1) {
            this.task.categories[existingIndex].type = type
          }
        }
        if (type === 'main') {
          if (category.addon) {
            category.addon = false
            array.removeById(this.task.categories, { id: category.id, type: 'addon' })
          }
        } else if (type === 'addon') {
          if (category.main) {
            category.main = false
            array.removeById(this.task.categories, { id: category.id, type: 'main' })
          }
        }
      } else {
        array.removeById(this.task.categories, { id: category.id, type: type })
      }
    },
    getSubCategories (category) {
      this.subCategories = []
      this.loading = true
      axios.get('/api/categories').then(response => {
        if (response.data && response.data.flat_categories) {
          this.subCategories = response.data.flat_categories.filter(cat => cat.parent_id === category.id)
          this.subCategories.forEach(subCategory => {
            subCategory.main = false
            subCategory.addon = false
          })
        } else {
          this.subCategories = []
        }
        if (this.task.categories && Array.isArray(this.task.categories)) {
          this.task.categories.forEach(taskCategory => {
            this.subCategories.forEach(subCategory => {
              if (subCategory.id === taskCategory.id) {
                if (taskCategory.type === 'main') {
                  subCategory.main = true
                  subCategory.addon = false
                } else if (taskCategory.type === 'addon') {
                  subCategory.addon = true
                  subCategory.main = false
                }
              }
            })
          })
        }
        this.loading = false
      }).catch(() => {
        this.subCategories = []
        this.loading = false
      })
    },
    searchMaterials () {
      if (this.disabled) return
      if (this.searchTimeout) clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        if (this.materialSearch && this.materialSearch.length >= 4) {
          this.loading = true
          this.search = true
          if (this.isCalculated) {
            Promise.all([
              axios.post('/product/fetch/search?type=default', { text: this.materialSearch }),
              axios.post('/product/fetch/search?type=company', { text: this.materialSearch })
            ]).then(([defaultResponse, companyResponse]) => {
              this.loading = false
              const combined = [
                ...(((defaultResponse.data && defaultResponse.data.default) || [])),
                ...(((defaultResponse.data && defaultResponse.data.company) || [])),
                ...(((companyResponse.data && companyResponse.data.default) || [])),
                ...(((companyResponse.data && companyResponse.data.company) || []))
              ]
              const dedupedById = combined.filter((item, index, arr) => {
                return arr.findIndex(x => x.id === item.id) === index
              })
              const selectedMaterialIds = new Set([
                ...((this.task.materials && this.task.materials.default) || []),
                ...((this.task.materials && this.task.materials.company) || [])
              ].map(m => m && m.id).filter(x => x != null))
              const filteredBySelection = dedupedById.filter(item => {
                return !(item && item.id != null && selectedMaterialIds.has(item.id))
              })
              this.materials = {
                default: normalizeForCalculatedIncludes(filteredBySelection).map(withNormalizedImages),
                company: []
              }
              this.resetHydratedIdsForUnresolved(this.materials.default)
              this.hydrateTaskItemImages()
            }).catch(() => {
              this.loading = false
              this.materials = { default: [], company: [] }
            })
          } else {
            axios.get('/api/materials', {
              params: { search: this.materialSearch, limit: 50 }
            }).then(response => {
              this.loading = false
              if (response.data && response.data.materials) {
                this.materials = { default: response.data.materials.map(withNormalizedImages), company: [] }
              } else {
                this.materials = { default: [], company: [] }
              }
              const selectedMaterialIds = new Set([
                ...((this.task.materials && this.task.materials.default) || []),
                ...((this.task.materials && this.task.materials.company) || [])
              ].map(m => m && m.id).filter(x => x != null))
              this.materials.default = (this.materials.default || []).filter(item => {
                return !(item && item.id != null && selectedMaterialIds.has(item.id))
              })
              this.resetHydratedIdsForUnresolved(this.materials.default)
              this.hydrateTaskItemImages()
            }).catch(() => {
              this.loading = false
              this.materials = { default: [], company: [] }
            })
          }
        } else {
          this.clearSearch(false)
        }
      }, 700)
    },
    searchVariables () {
      if (this.disabled) return
      if (this.searchTimeout) clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        if (this.variableSearch && this.variableSearch.length >= 3) {
          this.loading = true
          this.search = true
          Promise.all([
            axios.post('/product/fetch/search?type=default', { text: this.variableSearch }),
            axios.post('/product/fetch/search?type=company', { text: this.variableSearch })
          ]).then(([defaultResponse, companyResponse]) => {
            this.loading = false
            const combined = [
              ...(((defaultResponse.data && defaultResponse.data.default) || [])),
              ...(((defaultResponse.data && defaultResponse.data.company) || [])),
              ...(((companyResponse.data && companyResponse.data.default) || [])),
              ...(((companyResponse.data && companyResponse.data.company) || []))
            ]
            const deduped = combined.filter((item, index, arr) => {
              return arr.findIndex(x => x.id === item.id) === index
            })
            const selectedVarUuids = new Set([
              ...((this.task.variables && this.task.variables.default) || []),
              ...((this.task.variables && this.task.variables.company) || [])
            ].map(v => v && v.uuid).filter(x => x != null))
            const selectedVarIds = new Set([
              ...((this.task.variables && this.task.variables.default) || []),
              ...((this.task.variables && this.task.variables.company) || [])
            ].map(v => v && v.id).filter(x => x != null))
            this.variables = {
              default: deduped.filter(item => {
                if (!item) return false
                return !(selectedVarIds.has(item.id) || selectedVarUuids.has(item.uuid))
              }).map(withNormalizedImages),
              company: []
            }
            this.resetHydratedIdsForUnresolved(this.variables.default)
            this.hydrateTaskItemImages()
          }).catch(() => {
            this.loading = false
            this.variables = { default: [], company: [] }
          })
        } else {
          this.clearSearch(false)
        }
      }, 700)
    },
    resetHydratedIdsForUnresolved (list) {
      const base = this.$store.getters['getImageUrl']
      ;(list || []).forEach(item => {
        if (!item || item.id == null) return
        if (!itemHasResolvableImage(item, base)) {
          delete this.hydratedImageIds[item.id]
        }
      })
    },
    clearSearch (box = true) {
      this.search = false
      if (box) {
        this.materialSearch = ''
        this.variableSearch = ''
      }
      this.materials = { default: [], company: [] }
      this.variables = { default: [], company: [] }
    },
    calculateCost () {
      let materialCost = 0
      let productCost = 0
      let labourCost = 0
      this.task.cost = 0
      if (this.isTimed) {
        const hours = parseFloat(this.task.labour_time_hours) || 0
        const minutes = parseFloat(this.task.labour_time_minutes) || 0
        const totalHours = hours + minutes / 60
        const multiplier = parseFloat(this.task.hourly_rate_multiplier) || 1
        labourCost = totalHours * multiplier * this.effectiveHourlyRate()
      }
      ;((this.task.materials && this.task.materials.default) || []).forEach(material => {
        if (material.qty) materialCost += this.sellPriceIncGst(material) * parseFloat(material.qty)
      })
      ;((this.task.materials && this.task.materials.company) || []).forEach(material => {
        if (material.qty) materialCost += this.sellPriceIncGst(material) * parseFloat(material.qty)
      })
      ;((this.task.products && this.task.products.default) || []).forEach(product => {
        if (product.sell_price && product.qty) {
          productCost += parseFloat(product.sell_price) * parseFloat(product.qty)
        }
      })
      ;((this.task.products && this.task.products.company) || []).forEach(product => {
        if (product.sell_price && product.qty) {
          productCost += parseFloat(product.sell_price) * parseFloat(product.qty)
        }
      })
      this.task.cost = parseFloat((labourCost + materialCost + productCost).toFixed(2))
    },
    deleted () {
      if (!this.task.id) return
      this.swal({
        title: 'Are you sure?',
        text: 'Delete (' + (this.task.code || this.task.title) + ') from your tasks',
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
      }).then((result) => {
        if (result.value) {
          axios.delete('/api/tasks/' + this.task.id).then(() => {
            this.successNotification('Task deleted')
            this.$router.push('/settings/task')
          }).catch(() => {
            this.errorNotification('Failed to delete task')
          })
        }
      })
    }
  }
}
</script>

<style scoped>
.page-with-subnav {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 48px);
  min-height: calc(100dvh - 48px);
}
.page-with-subnav__content.main-container {
  flex: 1 0 auto;
  /* Global mobile .main-container adds 16px side margins; auto width keeps
     those margins inside the viewport instead of adding them to 100%. */
  width: auto;
  margin-top: 16px;
  /* Reserve space for the viewport-fixed action footer. */
  padding-bottom: 104px;
  box-sizing: border-box;
}
.task-form-grid {
  max-width: 680px;
  margin-left: 0;
  margin-right: 0;
}
.task-form-heading {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 0;
  color: #323C47;
}
.task-form-inner {
  width: 100%;
}
@media (min-width: 768px) {
  .task-form-inner {
    max-width: none;
  }
}
.task-type-help-link {
  display: inline-block;
  margin-top: 8px;
  color: #4DA1FF;
  font-size: 14px;
  line-height: 1.4;
  text-decoration: underline;
}
.task-type-help-link:hover,
.task-type-help-link:focus {
  color: var(--color-primary);
  text-decoration: underline;
}
.task-management-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-top: 28px;
}
.task-reset-default-btn {
  display: inline-flex !important;
  align-items: center;
  gap: 8px;
  color: #FF4C4C !important;
  border-color: #FF4C4C !important;
  box-shadow: 0 0 0 1px #FF4C4C inset !important;
}
.task-reset-default-btn:hover,
.task-reset-default-btn:focus {
  color: #FF4C4C !important;
  border-color: #FF4C4C !important;
  box-shadow: 0 0 0 1px #FF4C4C inset !important;
}
.task-reset-default-btn__icon {
  width: 16px;
  height: 16px;
  display: block;
  flex: 0 0 auto;
  background-color: #FF4C4C;
  -webkit-mask: url('../../assets/img/icn_refresh.png') center / contain no-repeat;
  mask: url('../../assets/img/icn_refresh.png') center / contain no-repeat;
}
.task-reset-default-btn.disabled .task-reset-default-btn__icon,
.task-reset-default-btn:disabled .task-reset-default-btn__icon {
  opacity: 0.45;
}
.classification-checkbox {
  margin-bottom: 4px;
}
.task-excavation-empty {
  padding: 24px 0;
  color: var(--color-mid-grey);
  text-align: center;
}
.task-stepper {
  margin: 8px 0 28px;
}

.task-stepper__row {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;

  --step-circle: 36px;
  --step-connector-height: 2px;
  --step-connector-end-gap: 16px;
}

.task-stepper__btn {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  user-select: none;
  flex: 0 0 auto;
  min-width: 0;
}

.task-stepper__num {
  width: var(--step-circle);
  height: var(--step-circle);
  border-radius: 50%;
  border: 2px solid #707070;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  background: #F9FAFB;
  color: #707070;
  box-sizing: border-box;
}

.task-stepper__check {
  font-size: 14px;
  line-height: 1;
  color: inherit;
}

.task-stepper__label {
  font-size: 14px;
  font-weight: 400;
  color: var(--color-text);
  line-height: 1;
  white-space: nowrap;
}

/* Selected (active) step: number highlighted, label stays theme text. */
.task-stepper__btn.is-active .task-stepper__num {
  border-color: #4DA1FF;
  color: #ffffff;
  background: #4DA1FF;
}

.task-stepper__btn.is-next .task-stepper__num {
  border-color: #4DA1FF;
  color: #4DA1FF;
  background: var(--color-bg-input);
}
.task-stepper__btn.is-active .task-stepper__label {
  color: var(--color-text);
  font-weight: 600;
}

/* Completed (done) step: filled circle + check mark. */
.task-stepper__btn.is-done .task-stepper__num {
  border-color: #4DA1FF;
  background: #4DA1FF;
  color: #ffffff;
}
.task-stepper__btn.is-done .task-stepper__label {
  color: var(--color-text);
  font-weight: 400;
}

/* Connector segment between steps. */
.task-stepper__connector {
  flex: 1 1 auto;
  height: 0;
  margin: 0 var(--step-connector-end-gap);
  border-top: var(--step-connector-height) dashed #707070;
  box-sizing: border-box;
}

.task-stepper__connector.is-solid {
  border-top-style: solid;
  border-top-color: #4DA1FF;
}

@media screen and (max-width: 767px) {
  .task-stepper__row {
    --step-circle: 24px;
    --step-connector-height: 2px;
    --step-connector-end-gap: 8px;
    /* Buttons include a label below the circle. Align from the top so
       connectors can target the circle centre, not the whole button centre. */
    align-items: flex-start;
  }

  .task-stepper__btn {
    flex-direction: column;
    min-width: 0;
    flex: 1 1 0;
    gap: 8px;
  }

  .task-stepper__label {
    font-size: 13px;
    line-height: 1.2;
    white-space: normal;
    text-align: center;
  }

  .task-stepper__connector {
    margin-top: calc(var(--step-circle) / 2 - var(--step-connector-height) / 2);
    margin-bottom: 0;
  }
}

.task-step-panel {
  min-height: 120px;
}
.task-time-complete {
  display: flex;
  align-items: stretch;
  gap: 12px;
  width: 100%;
}
.task-time-complete > .smardie-field-control,
.task-time-complete > .select {
  flex: 1 1 0;
  min-width: 0;
  width: auto;
}
</style>
